/**
 * useTenants — Sprint 3.9
 *
 * Debounced typeahead fetch against /api/v3/tenants/list.
 * Scoped by the current property selection from the filter context.
 *
 *  - returns `{ tenants, loading, error }`
 *  - `query` is trimmed; an empty query returns a short popular list (the
 *    endpoint sorts by store name so this is effectively "first 20 A–Z").
 *  - debounce + AbortController mirror the EntitySearchPicker / useWidgetData
 *    pattern so rapid typing doesn't fan-out requests.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { v3Api } from '../../hooks/useApi';

const DEBOUNCE_MS = 220;

export function useTenants(propertyIds, query, { paused = false, limit = 20 } = {}) {
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const propsCsv = useMemo(() => (propertyIds || []).slice().sort().join(','), [propertyIds]);
    const trimmedQuery = (query || '').trim();
    const lastReqRef = useRef(0);

    useEffect(() => {
        if (paused) return;

        const ac = new AbortController();
        const reqId = ++lastReqRef.current;

        const t = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const params = { limit };
                if (propsCsv) params.property_id = propsCsv;
                if (trimmedQuery) params.q = trimmedQuery;
                const res = await v3Api.tenants.list(params, { signal: ac.signal });
                if (ac.signal.aborted || reqId !== lastReqRef.current) return;
                const rows = Array.isArray(res?.data) ? res.data : [];
                setTenants(rows);
            } catch (err) {
                if (ac.signal.aborted) return;
                if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') return;
                setError(err.response?.data?.message || err.message || 'Failed to load tenants');
            } finally {
                if (!ac.signal.aborted) setLoading(false);
            }
        }, DEBOUNCE_MS);

        return () => {
            clearTimeout(t);
            ac.abort();
        };
    }, [propsCsv, trimmedQuery, paused, limit]);

    return { tenants, loading, error };
}
